import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from "@angular/core";
import * as moment from "moment";
import { DateRange, Ranges } from "@app/helpers/ranges";
import { Calendar } from "primeng/calendar";
import { Products } from "@app/core/Product";
import { ApiService } from "@app/services/api/api.service";
import { VesselGroupsService } from "@app/services/vessel-groups/vessel-groups.service";
import { Subscription } from "rxjs";
import { RangeService } from "@app/services/ranges/range-service";

@Component({
  selector: "app-calendar",
  templateUrl: "./calendar.component.html",
  styleUrls: ["./calendar.component.scss"],
})
export class CalendarComponent implements OnInit, OnDestroy {
  @ViewChild("calendar") calendarPopup!: Calendar;
  @Input() isVoyageMode: boolean = false;
  @Input() rangeService: RangeService;
  @Input() showResetButton = false;
  @Output() dateRange = new EventEmitter<DateRange>();

  isLast24HoursSelected: boolean = false;
  isLast7DaysSelected: boolean = false;
  isLast30DaysSelected: boolean = false;
  isThisMonthSelected: boolean = false;
  isLastMonthSelected: boolean = false;

  selectedLocalDates: Date[] = [];
  activeLocalDates: Date[] = [];
  activePreset: string;
  numberOfMonths = 2;

  minDate: Date;
  maxDate: Date;

  private subscriptions: Subscription[] = [];

  constructor(
    private apiService: ApiService,
    private vesselGroupsService: VesselGroupsService,
  ) {
  }

  @HostListener("window:resize", ["$event"])
  onResize(event: UIEvent): void {
    const width = (event.target as Window).innerWidth;
    this.updateNumberOfMonths(width);
  }

  ngOnInit(): void {
    this.updateNumberOfMonths(window.innerWidth);

    this.apiService
      .initialize()
      .toPromise()
      .then(() => {
        this.subscriptions.push(
          this.vesselGroupsService.getSelectedVesselObservable().subscribe({
            next: (selectedVessel) => {
              if (!selectedVessel) {
                return;
              }
              const product = Products.getProduct(selectedVessel.id);
              const today = moment().endOf("day");
              this.maxDate = today.toDate();
              if (!this.isVoyageMode) {
                this.minDate = moment().subtract(product.dataLimitDays, "days").startOf("day").toDate();
              }
            },
          }),
          this.rangeService.getRangeSubject().subscribe({
            next: (dateRange) => {
              this.updateFromService(dateRange);
            },
          }),
        );
      });
  }

  ngOnDestroy(): void {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
  }

  onSelect() {
    if (this.selectedLocalDates[0] != null && this.selectedLocalDates[1] != null) {
      const rangeStartUnix24Hours = moment(this.selectedLocalDates[0]).unix();
      const rangeStartUnix = moment(this.selectedLocalDates[0]).startOf("day").unix();
      const rangeEndUnix = moment(this.selectedLocalDates[1]).endOf("day").unix();
      // consider big delay between selecting both values
      if (Math.abs(rangeStartUnix24Hours - moment().subtract(1, "days").unix()) <= 60 * 60 && rangeEndUnix == moment().endOf("day").unix()) {
        this.selectPreset("Last 24 Hours");
      } else if (rangeStartUnix == moment().subtract(6, "days").startOf("day").unix() && rangeEndUnix == moment().endOf("day").unix()) {
        this.selectPreset("Last 7 Days");
      } else if (rangeStartUnix == moment().subtract(29, "days").startOf("day").unix() && rangeEndUnix == moment().endOf("day").unix()) {
        this.selectPreset("Last 30 Days");
      } else if (rangeStartUnix == moment().startOf("month").unix() && rangeEndUnix == moment().endOf("day").unix()) {
        this.selectPreset("This Month");
      } else if (
        rangeStartUnix == moment().subtract(1, "month").startOf("month").unix() &&
        rangeEndUnix == moment().subtract(1, "month").endOf("month").unix()
      ) {
        this.selectPreset("Last Month");
      } else {
        this.selectPreset(null);
      }
    } else {
      this.selectPreset(null);
    }
  }

  selectPreset(range: string) {
    this.isLast24HoursSelected = range === "Last 24 Hours";
    this.isLast7DaysSelected = range === "Last 7 Days";
    this.isLast30DaysSelected = range === "Last 30 Days";
    this.isThisMonthSelected = range === "This Month";
    this.isLastMonthSelected = range === "Last Month";
    if (range != null) {
      this.setSelectedDateFromPrefix(range);
    }
  }

  onDateUpdate() {
    if (this.calendarPopup.overlayVisible) {
      this.calendarPopup.overlayVisible = false;
    }

    let result;
    if (this.hasSelectedPrefix()) {
      result = Object.assign(Ranges.byName(this.getSelectedPrefix()));
    } else {
      const start = moment(this.selectedLocalDates[0]);
      const end = this.selectedLocalDates[1] == null ? moment(this.selectedLocalDates[0]).endOf("day") : moment(this.selectedLocalDates[1]);
      result = {
        start: this.toUtc(start).unix(),
        end: this.toUtc(end).unix(),
      };
    }
    this.dateRange.emit(result);
  }

  isDateOutOfRange(date: any): boolean {
    const calendarDate = new Date(date.year, date.month, date.day);
    return calendarDate > this.maxDate || calendarDate < this.minDate;
  }

  setPopupState(isVisible: boolean): void {
    if (!isVisible) {
      // reset values
      this.selectedLocalDates = this.activeLocalDates;
      this.selectPreset(this.activePreset);
    }
  }

  onResetClicked() {
    this.selectedLocalDates = null;
    this.dateRange.emit({ start: null, end: null });
  }

  private updateFromService(dateRange: DateRange) {
    this.selectPreset(dateRange.name);
    if (!dateRange.name) {
      this.selectedLocalDates = [dateRange.start != null ? this.toLocal(moment.unix(dateRange.start)).toDate() : null,
        dateRange.end != null ? this.toLocal(moment.unix(dateRange.end)).toDate() : null];
    }
    this.activeLocalDates = this.selectedLocalDates;
    this.activePreset = dateRange.name;
  }

  private setSelectedDateFromPrefix(prefix: string): void {
    let startDate: moment.Moment;
    let endDate: moment.Moment = moment().endOf("day");

    switch (prefix) {
      case "Last 24 Hours":
        startDate = moment().subtract(1, "days");
        break;
      case "Last 7 Days":
        startDate = moment().subtract(6, "days").startOf("day");
        break;
      case "Last 30 Days":
        startDate = moment().subtract(29, "days").startOf("day");
        break;
      case "This Month":
        startDate = moment().startOf("month");
        break;
      case "Last Month":
        startDate = moment().subtract(1, "month").startOf("month");
        endDate = moment().subtract(1, "month").endOf("month");
        break;
      default:
        startDate = moment();
    }
    this.selectedLocalDates = [startDate.toDate(), endDate.toDate()];
  }

  private toUtc(date: moment.Moment): moment.Moment {
    return date.clone().add(date.utcOffset(), "minutes").utc();
  }

  private toLocal(date: moment.Moment): moment.Moment {
    return date.clone().subtract(date.utcOffset(), "minutes");
  }

  private updateNumberOfMonths(width: number) {
    this.numberOfMonths = width < 768 ? 1 : 2;
  }

  private hasSelectedPrefix() {
    return this.isLast24HoursSelected ||
      this.isLast7DaysSelected ||
      this.isLast30DaysSelected ||
      this.isThisMonthSelected ||
      this.isLastMonthSelected;
  }

  private getSelectedPrefix(): string {
    return this.isLast24HoursSelected ? "Last 24 Hours" :
      this.isLast7DaysSelected ? "Last 7 Days" :
        this.isLast30DaysSelected ? "Last 30 Days" :
          this.isThisMonthSelected ? "This Month" :
            this.isLastMonthSelected ? "Last Month" : "";
  }
}
